import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { Plus20Px, SquareIconButton } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  FieldArrayWithId,
  useFieldArray,
  useFormContext
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import {
  firstUnusedGridPositionAmongPortsAndTotes,
  getEffectiveGridPortIds,
  getSelectablePortIdsForWorkstationFormRow,
  mergeMultiportsWithFormParentIds,
  pickDefaultPortIdForNewPortRow
} from "~/features/preview/workstation/settingsWorkstation.logic";
import { workstationPreviewConfigCardMaxWidthPx } from "~/features/preview/workstation/workstation.styles";
import {
  portTypes,
  portDirections,
  binSizes,
  configurationOptions,
  possibleGridCoordinates
} from "~/features/preview/workstation/workstationConstants";
import {
  settingsControlFontSize,
  settingsInputMinHeightPx,
  settingsOverviewLabelColumnPx,
  settingsOverviewRowGridTemplateColumns
} from "~/features/settings/settings.styles";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import {
  selectDropdownWorkstationAutostoreGrid,
  selectDropdownWorkstation
} from "~/redux/selectors/workstationConfigSelectors";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import {
  useGetMultiportsQuery,
  useGetWorkstationsQuery
} from "~/redux/warehouse/workstation.hooks";
import { UpdateWorkstation, WorkstationPortRequestDto } from "~/types/api";

const addPortIconButtonSx = {
  width: settingsInputMinHeightPx,
  height: settingsInputMinHeightPx,
  minWidth: settingsInputMinHeightPx,
  minHeight: settingsInputMinHeightPx,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
} as const;

const portControlSx: SxProps<Theme> = {
  minHeight: settingsInputMinHeightPx,
  fontSize: settingsControlFontSize,
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    minHeight: settingsInputMinHeightPx,
    boxSizing: "border-box",
    fontSize: settingsControlFontSize,
    lineHeight: 1.35,
    paddingRight: "36px"
  }
};

export function PortLabeledRow({
  label,
  htmlFor,
  children
}: {
  label: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        justifyContent: { xs: "stretch", sm: "start" },
        justifyItems: { xs: "stretch", sm: "start" },
        gridTemplateColumns: settingsOverviewRowGridTemplateColumns,
        columnGap: { xs: 1, sm: 1.5 },
        rowGap: 1.25,
        alignItems: { xs: "stretch", sm: "center" },
        width: "100%"
      }}
    >
      <Typography
        component="label"
        htmlFor={htmlFor}
        variant="body2"
        sx={{
          color: "text.primary",
          fontSize: settingsControlFontSize,
          lineHeight: 1.35,
          minWidth: 0,
          width: { xs: "100%", sm: `${settingsOverviewLabelColumnPx}px` },
          maxWidth: { sm: `${settingsOverviewLabelColumnPx}px` },
          whiteSpace: { sm: "normal" },
          alignSelf: { xs: "flex-start", sm: "center" },
          pr: { xs: 0, sm: 0.5 },
          boxSizing: "border-box"
        }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, width: "100%", maxWidth: "100%" }}>
        {children}
      </Box>
    </Box>
  );
}

export function PortSetup() {
  const { t } = useTranslation();
  const [noFreePortsDialogOpen, setNoFreePortsDialogOpen] = useState(false);
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const workstation = useAppSelector(selectDropdownWorkstation);
  const workstationAutostoreGrid = useAppSelector(
    selectDropdownWorkstationAutostoreGrid
  );
  const firstSitePort = workstation?.ports?.[0];

  const { data: autostoreGrids } = useGetAutostoreGridsQuery();
  const { data: workstations } = useGetWorkstationsQuery();
  const { data: multiports } = useGetMultiportsQuery(
    workstation?.autostoreGridId || skipToken
  );

  const effectiveGridPortIds = getEffectiveGridPortIds(
    workstationAutostoreGrid,
    workstations,
    workstation?.autostoreGridId,
    workstation
  );

  const { ws_addRemovePortsEnabled, ws_modifyPortSetupEnabled } =
    useAppSelector(selectServerConfig);

  const shouldShowParentPortId =
    firstSitePort?.configuration?.toLowerCase() === "multiport";

  const serverPortsSignature = useMemo(
    () =>
      workstation == null
        ? ""
        : `${workstation.id}:${JSON.stringify(
            workstation.ports?.map((p) => ({
              portId: p.portId,
              parentPortId: p.parentPortId,
              coordinate: p.coordinate
            })) ?? []
          )}`,
    [workstation]
  );

  const [persistedMultiportRowFieldIds, setPersistedMultiportRowFieldIds] =
    useState<Set<string>>(() => new Set());
  const previousServerPortsSignatureRef = useRef<string>("");

  const {
    control,
    formState,
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues
  } = useFormContext<UpdateWorkstation>();
  const { errors, isDirty } = formState;
  const {
    fields: ports,
    append: addPort,
    remove: removePort
  } = useFieldArray({
    control,
    name: "ports"
  });

  const watchedPorts = watch("ports");
  const multiportsForParentSelect = mergeMultiportsWithFormParentIds(
    multiports,
    watchedPorts
  );

  useLayoutEffect(() => {
    if (!serverPortsSignature) {
      previousServerPortsSignatureRef.current = "";
      setPersistedMultiportRowFieldIds(new Set());
      return;
    }

    const signatureChanged =
      serverPortsSignature !== previousServerPortsSignatureRef.current;
    previousServerPortsSignatureRef.current = serverPortsSignature;

    if (signatureChanged) {
      setPersistedMultiportRowFieldIds(new Set(ports.map((field) => field.id)));
      return;
    }

    setPersistedMultiportRowFieldIds((prev) => {
      if (prev.size === 0 && ports.length > 0) {
        return new Set(ports.map((field) => field.id));
      }
      return prev;
    });
  }, [serverPortsSignature, ports]);

  useEffect(() => {
    if (ports?.length === 0 && isDirty) {
      setError("ports", { type: "required" });
    } else if (ports?.length > 0) {
      clearErrors("ports");
    }
  }, [ports?.length, isDirty, setError, clearErrors]);

  const createNewPortTemplate = (): WorkstationPortRequestDto | null => {
    const existing = getValues("ports") ?? [];
    const totes = getValues("totePlacements") ?? [];
    const existingIds = existing.map((p) => p.portId);
    const portId = pickDefaultPortIdForNewPortRow(
      effectiveGridPortIds,
      workstations,
      workstation?.autostoreGridId,
      workstation?.id,
      existingIds
    );
    if (portId === null) {
      return null;
    }
    const first = existing[0];
    const isMultiport = first?.configuration?.toLowerCase() === "multiport";
    const coordinate = firstUnusedGridPositionAmongPortsAndTotes(
      existing.map((p) => p.coordinate),
      totes.map((t) => t.coordinate)
    );
    return {
      portId,
      configuration: isMultiport ? first.configuration : portTypes[0],
      binSize: binSizes[0],
      coordinate,
      inductionBinConfiguration: ["Whole Bin"],
      cleaningDirection: "No Direction",
      cells: [{ x: 0, y: 0 }],
      ...(isMultiport && first?.parentPortId != null
        ? { parentPortId: first.parentPortId }
        : {})
    };
  };

  const portRow = (
    portField: FieldArrayWithId<UpdateWorkstation, "ports">,
    index: number
  ) => {
    const showBinConfiguration =
      Boolean(workstation?.ports?.length) &&
      firstSitePort?.configuration !== "cleaning";

    const portIdSelectId = `ps-port-id-select-${index}`;
    const binSelectId = `ps-bin-configuration-select-${index}`;
    const typeSelectId = `ps-port-type-select-${index}`;
    const directionSelectId = `ps-port-direction-select-${index}`;
    const parentSelectId = `ps-parent-port-select-${index}`;
    const xSelectId = `ps-x-coordinate-select-${index}`;
    const ySelectId = `ps-y-coordinate-select-${index}`;

    const formPortIds = watch("ports")?.map((p) => p.portId) ?? [];
    const selectablePortIds = getSelectablePortIdsForWorkstationFormRow(
      effectiveGridPortIds,
      workstations,
      workstation?.autostoreGridId,
      workstation?.id,
      formPortIds,
      index
    );

    const formPortCount = ports.length;
    const insufficientPortsForCleaningFusion = formPortCount < 2;
    const rowConfiguration = watch(`ports.${index}.configuration`, "");
    const rowParentPortId = watch(`ports.${index}.parentPortId`);
    const lockPortIdentityForRow =
      rowParentPortId != null &&
      persistedMultiportRowFieldIds.has(portField.id);

    return (
      <Stack spacing={1.5} sx={{ width: "100%" }}>
        <PortLabeledRow label={t("port id")} htmlFor={portIdSelectId}>
          {!!autostoreGrids?.length && (
            <FormControl
              fullWidth
              size="small"
              sx={{ minWidth: 152, width: "100%" }}
            >
              <Select
                id={portIdSelectId}
                labelId={`ps-port-id-${index}`}
                fullWidth
                variant="outlined"
                disabled={!isAdmin}
                {...register(`ports.${index}.portId`)}
                value={watch(`ports.${index}.portId`, 0)}
                sx={portControlSx}
              >
                {selectablePortIds.map((pid) => (
                  <MenuItem key={pid} value={pid}>
                    {`${pid}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </PortLabeledRow>

        <PortLabeledRow label={t("port type")} htmlFor={typeSelectId}>
          <FormControl fullWidth size="small">
            <Select
              id={typeSelectId}
              disabled={!ws_modifyPortSetupEnabled || !isAdmin}
              labelId={`ps-port-type-${index}`}
              variant="outlined"
              fullWidth
              {...register(`ports.${index}.configuration`)}
              value={watch(`ports.${index}.configuration`, "")}
              sx={portControlSx}
            >
              {portTypes.map((numOption) => {
                const opt = numOption?.toLowerCase() ?? "";
                // Cleaning and Fusion need two physical ports; hide those options when only one row exists.
                const disableCleaningFusionUntilSecondPort =
                  insufficientPortsForCleaningFusion &&
                  (opt === "cleaning" || opt === "fusion");
                // With one port row, switching away from Cleaning would leave an invalid setup — stay on Cleaning until a second port is added.
                const blockLeavingCleaningUntilSecondPort =
                  insufficientPortsForCleaningFusion &&
                  rowConfiguration?.toLowerCase() === "cleaning" &&
                  opt !== "cleaning";
                const isDisabled =
                  disableCleaningFusionUntilSecondPort ||
                  blockLeavingCleaningUntilSecondPort;
                const hintTitle =
                  disableCleaningFusionUntilSecondPort && opt === "cleaning"
                    ? t("port type disabled cleaning needs two ports")
                    : disableCleaningFusionUntilSecondPort && opt === "fusion"
                      ? t("port type disabled fusion needs two ports")
                      : blockLeavingCleaningUntilSecondPort
                        ? t("port type disabled cannot leave cleaning")
                        : undefined;
                return (
                  <MenuItem
                    key={numOption}
                    value={numOption}
                    disabled={isDisabled}
                    title={hintTitle}
                  >
                    {`${numOption}`}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </PortLabeledRow>

        {Boolean(workstation?.ports?.length) &&
          firstSitePort?.configuration === "cleaning" && (
            <PortLabeledRow
              label={t("port direction")}
              htmlFor={directionSelectId}
            >
              <FormControl fullWidth size="small">
                <Select
                  id={directionSelectId}
                  labelId={`ps-port-direction-${index}`}
                  variant="outlined"
                  fullWidth
                  {...register(`ports.${index}.cleaningDirection`)}
                  value={
                    watch(`ports.${index}.cleaningDirection`) ?? "No Direction"
                  }
                  sx={portControlSx}
                >
                  {portDirections.map((direction) => (
                    <MenuItem
                      key={direction}
                      value={direction}
                      disabled={
                        firstSitePort?.configuration === "cleaning" &&
                        direction === "No Direction"
                      }
                    >
                      {direction}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </PortLabeledRow>
          )}

        {shouldShowParentPortId && (
          <PortLabeledRow label={t("parent port id")} htmlFor={parentSelectId}>
            <FormControl fullWidth size="small">
              <Select
                id={parentSelectId}
                disabled={!ws_modifyPortSetupEnabled || !isAdmin}
                labelId={`ps-parent-port-id-${index}`}
                variant="outlined"
                fullWidth
                value={watch(`ports.${index}.parentPortId`) ?? "none"}
                sx={portControlSx}
                onChange={(e) => {
                  const value =
                    e.target.value === "none"
                      ? undefined
                      : Number(e.target.value);
                  ports.forEach((_, i) => {
                    setValue(`ports.${i}.parentPortId`, value, {
                      shouldDirty: true
                    });
                  });
                }}
              >
                <MenuItem key={`none-${index}`} value="none">
                  {t("none")}
                </MenuItem>
                {multiportsForParentSelect.map((multiport) => (
                  <MenuItem key={multiport.portId} value={multiport.portId}>
                    {multiport.portId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </PortLabeledRow>
        )}

        {showBinConfiguration && (
          <PortLabeledRow label={t("bin configuration")} htmlFor={binSelectId}>
            <FormControl fullWidth size="small">
              <Select
                id={binSelectId}
                labelId={`ps-bin-configuration-${index}`}
                fullWidth
                variant="outlined"
                {...register(`ports.${index}.inductionBinConfiguration`)}
                value={
                  watch(`ports.${index}.inductionBinConfiguration`)?.length
                    ? watch(`ports.${index}.inductionBinConfiguration`)
                    : ""
                }
                sx={portControlSx}
                onChange={(e) => {
                  const values = [e.target.value as string];
                  setValue(`ports.${index}.inductionBinConfiguration`, values, {
                    shouldDirty: true
                  });
                }}
              >
                {configurationOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </PortLabeledRow>
        )}

        <PortLabeledRow
          label={t("layout coordinate x", {
            defaultValue: "X"
          })}
          htmlFor={xSelectId}
        >
          <FormControl fullWidth size="small">
            <Select
              id={xSelectId}
              labelId={`ps-x-coordinate-${index}`}
              fullWidth
              variant="outlined"
              disabled={!isAdmin || lockPortIdentityForRow}
              {...register(`ports.${index}.coordinate.x`)}
              value={watch(`ports.${index}.coordinate.x`, 0)}
              sx={portControlSx}
            >
              {possibleGridCoordinates.map((numOption) => (
                <MenuItem key={numOption} value={numOption}>
                  {`${numOption}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </PortLabeledRow>

        <PortLabeledRow
          label={t("layout coordinate y", {
            defaultValue: "Y"
          })}
          htmlFor={ySelectId}
        >
          <FormControl fullWidth size="small">
            <Select
              id={ySelectId}
              labelId={`ps-y-coordinate-${index}`}
              fullWidth
              variant="outlined"
              disabled={!isAdmin || lockPortIdentityForRow}
              {...register(`ports.${index}.coordinate.y`)}
              value={watch(`ports.${index}.coordinate.y`, 0)}
              sx={portControlSx}
            >
              {possibleGridCoordinates.map((numOption) => (
                <MenuItem key={numOption} value={numOption}>
                  {`${numOption}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </PortLabeledRow>
      </Stack>
    );
  };

  return (
    <Box id="ws-port-setup-root">
      <Stack
        id="ws-port-setup-row"
        direction="row"
        spacing={2}
        alignItems="stretch"
        sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}
      >
        <Box
          id="ws-port-setup-cards-wrap"
          role="list"
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: "24px",
            alignItems: "stretch",
            flex: "1 1 auto",
            minWidth: 0
          }}
        >
          {ports?.map((port, index) => (
            <Paper
              key={port.id}
              id={`ws-port-setup-card-${index}`}
              elevation={0}
              variant="outlined"
              role="listitem"
              sx={{
                flex: {
                  xs: "1 1 100%",
                  md: "0 1 auto"
                },
                width: { xs: "100%", md: "auto" },
                minWidth: 0,
                maxWidth: {
                  xs: "100%",
                  md: workstationPreviewConfigCardMaxWidthPx
                },
                boxSizing: "border-box",
                px: 1.5,
                py: 1.25,
                display: "flex",
                flexDirection: "column",
                gap: 1
              }}
            >
              <Box
                id={`ws-port-setup-card-${index}-fields`}
                sx={{ width: "100%", minWidth: 0 }}
              >
                {portRow(port, index)}
              </Box>
              {ws_addRemovePortsEnabled && isAdmin && (
                <Box
                  id={`ws-port-setup-card-${index}-toolbar`}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                    minHeight: 32,
                    pt: 0.5
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label={t("remove port")}
                    onClick={() => removePort(index)}
                    sx={{ mb: -0.5, mr: -0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
        {ws_addRemovePortsEnabled && isAdmin && (
          <Stack
            id="ws-port-setup-add-wrap"
            direction="column"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            sx={{ flexShrink: 0, alignSelf: "stretch" }}
          >
            <SquareIconButton
              aria-label={t("add port")}
              onClick={() => {
                const template = createNewPortTemplate();
                if (template === null) {
                  setNoFreePortsDialogOpen(true);
                  return;
                }
                addPort(template);
              }}
              size="medium"
              sx={addPortIconButtonSx}
            >
              <Plus20Px />
            </SquareIconButton>
          </Stack>
        )}
      </Stack>

      {errors.ports?.type === "required" && (
        <Typography color="error.main" my={2} fontWeight={700}>
          {t("at least one port is required")}
        </Typography>
      )}

      <Dialog
        open={noFreePortsDialogOpen}
        onClose={() => setNoFreePortsDialogOpen(false)}
      >
        <DialogTitle>{t("create workstation no free ports title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("create workstation no free ports detail")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setNoFreePortsDialogOpen(false)}
            autoFocus
          >
            {t("ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
